import { useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api.js';
import { Page } from '../../app/AppShell.js';
import { useToast } from '../../components/Toast.js';
import type { DisplayImportResult, XiboImportReport } from '../../lib/types.js';

export function ImportScreen(): ReactNode {
  return (
    <Page title="Import">
      <p className="muted small" style={{ marginBottom: 16 }}>
        Bring layouts and displays over from Xibo. See <code>docs/adr/0013</code> for what does and
        doesn't translate — schedules are not carried by a Xibo export and need rebuilding.
      </p>
      <XiboLayoutImport />
      <div style={{ height: 24 }} />
      <DisplayListImport />
    </Page>
  );
}

function XiboLayoutImport(): ReactNode {
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [report, setReport] = useState<XiboImportReport | null>(null);
  const [busy, setBusy] = useState(false);

  async function run(dryRun: boolean): Promise<void> {
    if (!file) return;
    setBusy(true);
    try {
      const result = await api.importXiboLayout(file, dryRun);
      setReport(result);
      if (!dryRun && result.ok) {
        toast.push('Layout imported as a draft', 'success');
      } else if (!dryRun) {
        toast.push('Import failed — see the errors below', 'error');
      }
    } catch (err) {
      toast.error(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card card-pad">
      <h2 style={{ marginTop: 0 }}>Xibo layout</h2>
      <p className="muted small">
        Upload a Xibo layout export <code>.zip</code> (Xibo 3.3+). It is previewed first; nothing is
        written until you confirm. Imported layouts land as <strong>drafts</strong>.
      </p>

      <div className="row" style={{ gap: 8, alignItems: 'center' }}>
        <input
          ref={fileRef}
          type="file"
          accept=".zip"
          onChange={(e) => {
            setFile(e.target.files?.[0] ?? null);
            setReport(null);
          }}
        />
        <button
          className="btn"
          disabled={!file || busy}
          onClick={() => void run(true)}
        >
          {busy ? 'Working…' : 'Preview'}
        </button>
      </div>

      {report ? (
        <div style={{ marginTop: 14 }}>
          <ReportView report={report} />
          <div className="row" style={{ marginTop: 12, gap: 8 }}>
            <button
              className="btn primary"
              disabled={busy || !report.ok}
              onClick={() => void run(false)}
            >
              {report.dryRun ? 'Import as draft' : 'Re-import'}
            </button>
            {report.layoutId ? (
              <Link className="btn" to={`/layouts/${report.layoutId}`}>
                Open in editor
              </Link>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function ReportView({ report }: { report: XiboImportReport }): ReactNode {
  return (
    <div className="stack small">
      <div className="row" style={{ gap: 16, flexWrap: 'wrap' }}>
        <Stat label="Regions" value={report.regionsMapped} />
        <Stat label="Widgets" value={`${report.widgetsMapped} mapped`} />
        <Stat label="Skipped" value={report.widgetsSkipped} tone={report.widgetsSkipped > 0 ? 'warn' : undefined} />
        <Stat label="Media" value={`${report.media.created} new · ${report.media.reused} reused`} />
        <Stat
          label="Missing media"
          value={report.media.missing}
          tone={report.media.missing > 0 ? 'warn' : undefined}
        />
      </div>

      {Object.keys(report.modules).length > 0 ? (
        <div className="faint">
          {Object.entries(report.modules)
            .map(([m, n]) => `${m}×${n}`)
            .join('  ')}
        </div>
      ) : null}

      {report.errors.length > 0 ? (
        <div className="form-error">
          <strong>Won't import:</strong>
          <ul style={{ margin: '4px 0 0 16px' }}>
            {report.errors.map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {report.warnings.length > 0 ? (
        <details>
          <summary className="muted">{report.warnings.length} warning(s)</summary>
          <ul style={{ margin: '4px 0 0 16px' }}>
            {report.warnings.map((w, i) => (
              <li key={i} className="muted">
                {w}
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string | number;
  tone?: 'warn';
}): ReactNode {
  return (
    <div>
      <div className="faint">{label}</div>
      <div className={tone === 'warn' ? 'badge warn' : ''} style={{ fontWeight: 600 }}>
        {value}
      </div>
    </div>
  );
}

function DisplayListImport(): ReactNode {
  const toast = useToast();
  const [text, setText] = useState('');
  const [result, setResult] = useState<DisplayImportResult | null>(null);
  const [busy, setBusy] = useState(false);

  const names = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);

  async function run(dryRun: boolean): Promise<void> {
    setBusy(true);
    try {
      const r = await api.importDisplays(names, dryRun);
      setResult(r);
      if (!dryRun) toast.push(`${r.created} display(s) created`, 'success');
    } catch (err) {
      toast.error(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card card-pad">
      <h2 style={{ marginTop: 0 }}>Displays</h2>
      <p className="muted small">
        One display name per line. Each is created <strong>pending</strong>; enrol the screen with a
        claim code afterwards. Existing names are skipped.
      </p>
      <textarea
        rows={5}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          setResult(null);
        }}
        placeholder={'Lobby screen\nCafé menu board\nMeeting room 3'}
        style={{ width: '100%', fontFamily: 'inherit' }}
      />
      <div className="row" style={{ marginTop: 8, gap: 8 }}>
        <button className="btn primary" disabled={busy || names.length === 0} onClick={() => void run(false)}>
          {busy ? 'Working…' : `Create ${names.length || ''} display${names.length === 1 ? '' : 's'}`}
        </button>
      </div>
      {result ? (
        <div className="small muted" style={{ marginTop: 8 }}>
          {result.created} created, {result.skipped} skipped.
        </div>
      ) : null}
    </div>
  );
}
